import React, { useEffect, useState } from "react";
import { Copy, Scissors, Trash2, RefreshCw, Loader2 } from "lucide-react";
import { api } from "@/api";
import { toast } from "sonner";
import { overrideProvenance } from "@/utils";

const fmtTs = (ms) => (ms ? new Date(ms).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit" }) : "—");
const fmtGap = (ms) => (ms < 1000 ? `${ms} ms` : ms < 60000 ? `${(ms / 1000).toFixed(1)} s` : ms < 3600000 ? `${Math.round(ms / 60000)} min` : `${(ms / 3600000).toFixed(1)} h`);

const Header = ({ title, sub, onRefresh, busy, slug }) => (
    <div className="flex items-center justify-between mb-3">
        <div>
            <h3 className="font-brand text-lg font-extrabold uppercase bg-gradient-to-r from-meye-fuchsia to-violet-400 bg-clip-text text-transparent tracking-wide">{title}</h3>
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{sub}</div>
        </div>
        <button onClick={onRefresh} disabled={busy} className="text-zinc-500 hover:text-meye-cyan" data-testid={`hygiene-refresh-${slug}`}>
            {busy ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
        </button>
    </div>
);

const DuplicateGroup = ({ group, onDeleted, onEdit }) => {
    const [confirmId, setConfirmId] = useState(null);
    const remove = async (id) => {
        try { await api.deleteTask(id); toast.success("Duplicate removed"); onDeleted(); }
        catch (e) { toast.error(e?.response?.data?.detail || e?.message || "Delete failed"); }
    };
    return (
        <div className="rounded-md border border-amber-400/30 bg-amber-500/5 p-3" data-testid={`dup-group-${group.fingerprint.slice(0, 8)}`}>
            <div className="text-sm font-semibold text-zinc-100 truncate">{group.text}</div>
            {group.description && <div className="text-xs text-zinc-500 line-clamp-1">{group.description}</div>}
            <div className="text-[10px] font-mono text-amber-300 mt-1">{group.count} identical records · created {fmtGap(group.gap_ms)} apart</div>
            <div className="mt-2 space-y-1">
                {group.records.map((r) => (
                    <div key={r.id} className="flex items-center gap-2 text-[11px] font-mono rounded bg-black/30 px-2 py-1.5" data-testid={`dup-record-${r.id}`}>
                        <button onClick={() => onEdit?.(r.id)} className="text-meye-cyan hover:underline truncate max-w-[110px]" title={r.id}>{r.id.slice(0, 10)}…</button>
                        <span className="text-zinc-400">{fmtTs(r.created_at)}</span>
                        <span className="text-zinc-500 truncate">{r.source || "unknown source"}{r.client_request_id ? " · req" : ""}</span>
                        {r.completions?.length > 0 && <span className="text-emerald-400">{r.completions.length} check-ins</span>}
                        <span className="flex-1" />
                        {r.keep_suggested ? (
                            <span className="text-emerald-400 uppercase tracking-widest text-[9px]">keep · oldest</span>
                        ) : confirmId === r.id ? (
                            <>
                                <button onClick={() => remove(r.id)} className="px-2 py-0.5 rounded bg-red-500/80 text-white text-[9px] uppercase tracking-widest" data-testid={`dup-delete-confirm-${r.id}`}>confirm delete</button>
                                <button onClick={() => setConfirmId(null)} className="text-zinc-500 text-[9px] uppercase">cancel</button>
                            </>
                        ) : (
                            <button onClick={() => setConfirmId(r.id)} className="flex items-center gap-1 text-red-300 hover:text-red-200 text-[9px] uppercase tracking-widest" data-testid={`dup-delete-${r.id}`}>
                                <Trash2 size={11} /> remove this copy
                            </button>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};

const DuplicatesSection = ({ onDataChanged, onEdit, refreshKey }) => {
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(false);
    const load = async () => {
        setBusy(true);
        try { setData(await api.taskDuplicates()); } catch (e) { toast.error("Couldn't load duplicates"); } finally { setBusy(false); }
    };
    useEffect(() => { load(); }, [refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps
    return (
        <section data-testid="hygiene-duplicates">
            <Header title="Duplicates" slug="duplicates" sub="identical title · body · date · time · type" onRefresh={load} busy={busy} />
            {data && data.count === 0 && <div className="text-xs font-mono text-zinc-500 py-4" data-testid="dup-empty">no duplicate task records</div>}
            <div className="space-y-2">
                {(data?.groups || []).map((g) => (
                    <DuplicateGroup key={g.fingerprint} group={g} onEdit={onEdit} onDeleted={() => { load(); onDataChanged?.(); }} />
                ))}
            </div>
        </section>
    );
};

const SplitSection = ({ onDataChanged, refreshKey }) => {
    const [data, setData] = useState(null);
    const [selected, setSelected] = useState(new Set());
    const [busy, setBusy] = useState(false);
    const [confirming, setConfirming] = useState(false);
    const load = async () => {
        setBusy(true);
        try {
            const d = await api.taskSplitCandidates();
            setData(d);
            setSelected(new Set(d.candidates.filter((c) => c.confidence === "high").map((c) => c.id)));
        } catch (e) { toast.error("Couldn't load split candidates"); } finally { setBusy(false); }
    };
    useEffect(() => { load(); }, [refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps
    const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
    const apply = async () => {
        const items = (data?.candidates || []).filter((c) => selected.has(c.id)).map((c) => ({ id: c.id, title: c.proposed_title, description: c.proposed_description }));
        if (!items.length) return;
        setBusy(true);
        try {
            const res = await api.applyTaskSplits(items);
            toast.success(`Split ${res.applied} task${res.applied === 1 ? "" : "s"} — originals kept in history`);
            setConfirming(false);
            await load();
            onDataChanged?.();
        } catch (e) { toast.error(e?.response?.data?.detail || e?.message || "Split failed"); } finally { setBusy(false); }
    };
    return (
        <section className="mt-8" data-testid="hygiene-splits">
            <Header title="Title + Body" slug="splits" sub="legacy tasks with heading and content in one line · high-confidence rows pre-selected, review the rest" onRefresh={load} busy={busy} />
            {data && data.count === 0 && <div className="text-xs font-mono text-zinc-500 py-4" data-testid="split-empty">no combined-text tasks found</div>}
            <div className="space-y-2">
                {(data?.candidates || []).map((c) => (
                    <label key={c.id} className={`flex gap-3 rounded-md border p-3 cursor-pointer ${selected.has(c.id) ? "border-meye-cyan/50 bg-meye-cyan/5" : "border-meye-border bg-black/20"}`} data-testid={`split-candidate-${c.id}`}>
                        <input type="checkbox" checked={selected.has(c.id)} onChange={() => toggle(c.id)} className="mt-1 accent-cyan-400" data-testid={`split-select-${c.id}`} />
                        <div className="min-w-0 flex-1">
                            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">current · {c.confidence} confidence</div>
                            <div className="text-xs text-zinc-400 line-clamp-2">{c.text}</div>
                            <div className="mt-1.5 text-[10px] font-mono uppercase tracking-widest text-meye-cyan">proposed</div>
                            <div className="text-sm font-semibold text-zinc-100 truncate">{c.proposed_title}</div>
                            <div className="text-xs text-zinc-500 line-clamp-2">{c.proposed_description}</div>
                        </div>
                    </label>
                ))}
            </div>
            {data?.count > 0 && (
                <div className="mt-3 flex items-center gap-3">
                    {confirming ? (
                        <>
                            <span className="text-xs font-mono text-amber-300" data-testid="split-confirm-count">change {selected.size} task{selected.size === 1 ? "" : "s"}?</span>
                            <button onClick={apply} disabled={busy || !selected.size} className="px-3 py-1.5 rounded bg-meye-cyan text-black text-xs font-brand font-bold uppercase tracking-widest disabled:opacity-40" data-testid="split-apply-confirm">yes, apply</button>
                            <button onClick={() => setConfirming(false)} className="text-xs text-zinc-500 uppercase tracking-widest">cancel</button>
                        </>
                    ) : (
                        <button onClick={() => setConfirming(true)} disabled={!selected.size} className="flex items-center gap-1.5 px-3 py-1.5 rounded border border-meye-cyan/50 text-meye-cyan text-xs font-brand font-bold uppercase tracking-widest disabled:opacity-40" data-testid="split-apply-selected">
                            <Scissors size={12} /> apply selected splits ({selected.size})
                        </button>
                    )}
                </div>
            )}
        </section>
    );
};

const KIND_STYLE = { CHAIN: "text-meye-cyan border-meye-cyan/40", EXACT_DUPLICATE: "text-zinc-300 border-zinc-500/40", ORPHAN: "text-amber-300 border-amber-400/40", CONFLICT: "text-red-300 border-red-400/50" };

const ChainSection = ({ onDataChanged, refreshKey }) => {
    const [data, setData] = useState(null);
    const [selected, setSelected] = useState(new Set());
    const [busy, setBusy] = useState(false);
    const [confirming, setConfirming] = useState(false);
    const load = async () => {
        setBusy(true);
        try {
            const d = await api.repairOverrideChains(null); // DRY RUN — changes nothing
            setData(d);
            setSelected(new Set(d.tasks.filter((t) => t.safe_to_repair).map((t) => t.id)));
        } catch (e) { toast.error("Couldn't scan moved occurrences"); } finally { setBusy(false); }
    };
    useEffect(() => { load(); }, [refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps
    const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
    const apply = async () => {
        setBusy(true);
        try {
            const res = await api.repairOverrideChains([...selected]);
            toast.success(`Normalized ${res.applied.length} task${res.applied.length === 1 ? "" : "s"}${res.blocked_conflicts.length ? ` · ${res.blocked_conflicts.length} conflict blocked` : ""}`);
            setConfirming(false); await load(); onDataChanged?.();
        } catch (e) { toast.error(e?.response?.data?.detail || e?.message || "Repair failed"); } finally { setBusy(false); }
    };
    const fmt = (l) => l.map((o) => `${o.from}→${o.to}${o.conflict ? " ⚠" : ""}`).join("  ·  ");
    return (
        <section className="mt-8" data-testid="hygiene-chains">
            <Header title="Moved Occurrences" slug="chains" sub="dry run · raw vs canonical · one source → one destination · conflicts are blocked for manual review" onRefresh={load} busy={busy} />
            {data && data.count === 0 && <div className="text-xs font-mono text-zinc-500 py-4" data-testid="chain-empty">every moved occurrence already points SOURCE → display</div>}
            <div className="space-y-2">
                {(data?.tasks || []).map((t) => {
                    const conflict = !t.safe_to_repair;
                    return (
                        <label key={t.id} className={`flex gap-3 rounded-md border p-3 ${conflict ? "border-red-400/40 bg-red-500/5 cursor-not-allowed" : selected.has(t.id) ? "border-meye-cyan/50 bg-meye-cyan/5 cursor-pointer" : "border-meye-border bg-black/20 cursor-pointer"}`} data-testid={`chain-task-${t.id}`}>
                            <input type="checkbox" disabled={conflict} checked={!conflict && selected.has(t.id)} onChange={() => toggle(t.id)} className="mt-1 accent-cyan-400" data-testid={`chain-select-${t.id}`} />
                            <div className="min-w-0 flex-1 text-xs">
                                <div className="flex items-center gap-1.5 flex-wrap">
                                    <span className="text-sm font-semibold text-zinc-100 truncate">{t.text}</span>
                                    {t.kinds.map((k) => <span key={k} className={`text-[9px] font-brand font-bold uppercase tracking-widest px-1.5 py-0.5 rounded border ${KIND_STYLE[k] || ""}`} data-testid={`chain-kind-${t.id}-${k}`}>{k.replace("_", " ")}</span>)}
                                </div>
                                <div className="font-mono text-zinc-500 mt-1 break-all">stored: {fmt(t.before)}</div>
                                <div className={`font-mono break-all ${conflict ? "text-red-300" : "text-meye-cyan"}`}>canonical: {fmt(t.after)}</div>
                                {(t.after || []).length > 0 && (
                                    <div className="font-mono text-zinc-500 mt-0.5 break-all" data-testid={`chain-provenance-${t.id}`}>
                                        provenance: {(t.after || []).map((o) => { const p = overrideProvenance(o); return `${o.from} ${p.by}/${p.reason}`; }).join(" · ")}
                                    </div>
                                )}
                                {conflict && <div className="font-mono text-red-300 mt-1" data-testid={`chain-conflict-${t.id}`}>BLOCKED · {[...(t.conflicts || []).map((c) => `${c.source} → ${c.destinations.join(" or ")}`), ...(t.ambiguities || []).map((a) => a.kind === "CYCLE" ? `cycle ${a.nodes.join("↔")}` : a.kind === "AMBIGUOUS_SHARED_INTERMEDIATE" ? `${a.sources.join(" & ")} share ${a.node} → ${a.onward.join("/")}` : `${a.source} → ${a.via} → ${a.destinations.join("/")} (legacy link)`)].join("; ")} — decide in the editor, then re-scan</div>}
                            </div>
                        </label>
                    );
                })}
            </div>
            {data?.count > 0 && (
                <div className="mt-3 flex items-center gap-3">
                    {confirming ? (
                        <>
                            <span className="text-xs font-mono text-amber-300" data-testid="chain-confirm-count">normalize {selected.size} task{selected.size === 1 ? "" : "s"}? (before-state is kept for undo)</span>
                            <button onClick={apply} disabled={busy || !selected.size} className="px-3 py-1.5 rounded bg-meye-cyan text-black text-xs font-brand font-bold uppercase tracking-widest disabled:opacity-40" data-testid="chain-apply-confirm">yes, apply</button>
                            <button onClick={() => setConfirming(false)} className="text-xs text-zinc-500 uppercase tracking-widest">cancel</button>
                        </>
                    ) : (
                        <button onClick={() => setConfirming(true)} disabled={!selected.size} className="px-3 py-1.5 rounded border border-meye-cyan/50 text-meye-cyan text-xs font-brand font-bold uppercase tracking-widest disabled:opacity-40" data-testid="chain-apply">
                            apply selected ({selected.size})
                        </button>
                    )}
                </div>
            )}
        </section>
    );
};

export const DataHygienePanel = ({ onDataChanged, onEdit, refreshKey }) => (
    <div data-testid="data-hygiene-panel">
        <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-4"><Copy size={12} /> cleanup · nothing here changes without your confirmation</div>
        <DuplicatesSection onDataChanged={onDataChanged} onEdit={onEdit} refreshKey={refreshKey} />
        <SplitSection onDataChanged={onDataChanged} refreshKey={refreshKey} />
        <ChainSection onDataChanged={onDataChanged} refreshKey={refreshKey} />
    </div>
);

export default DataHygienePanel;
